import {
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import {
  DEVICE_SESSION_STORAGE_KEY,
  PASSKEY_CREDENTIAL_HINT_KEY,
} from '../core/webauthn/rpConfig';
import { collectDeviceIdentity } from '../core/devices/deviceIdentity';
import { resolveDevicePlatform } from '../adapters/device/platformDetector';
import { callFunction } from './firebase/FunctionsService';

function writeDeviceStorage(storage: Storage | undefined, key: string, value: string | null) {
  if (!storage) return;
  try {
    if (value) storage.setItem(key, value);
    else storage.removeItem(key);
  } catch {
    // Private mode / quota.
  }
}

function sessionAndLocal(): Array<Storage | undefined> {
  return [
    typeof sessionStorage !== 'undefined' ? sessionStorage : undefined,
    typeof localStorage !== 'undefined' ? localStorage : undefined,
  ];
}

function readSessionToken(): string | null {
  for (const storage of sessionAndLocal()) {
    const value = storage?.getItem(DEVICE_SESSION_STORAGE_KEY);
    if (value) return value;
  }
  return null;
}

export function storeDeviceSession(token: string | undefined, credentialId?: string) {
  const value = token || null;
  for (const storage of sessionAndLocal()) {
    writeDeviceStorage(storage, DEVICE_SESSION_STORAGE_KEY, value);
  }
  if (credentialId) writeDeviceStorage(
    typeof localStorage !== 'undefined' ? localStorage : undefined,
    PASSKEY_CREDENTIAL_HINT_KEY,
    credentialId,
  );
}

export function clearDeviceSession() {
  for (const storage of sessionAndLocal()) {
    writeDeviceStorage(storage, DEVICE_SESSION_STORAGE_KEY, null);
  }
}

export function getStoredCredentialHint(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(PASSKEY_CREDENTIAL_HINT_KEY);
}

function denialError(err: unknown) {
  const message = String((err as Error)?.message ?? '');
  const details = (err as { details?: { code?: string; requiresPasskey?: boolean } })?.details;
  const code = String((err as { code?: string })?.code ?? '');
  const detailCode = details?.code ?? '';
  const lowered = `${message} ${code} ${detailCode}`.toLowerCase();
  if (
    code === 'auth/invalid-app-credential'
    || code.includes('app-check')
    || lowered.includes('app check')
    || lowered.includes('attestation')
    || (code === 'functions/failed-precondition' && lowered.includes('app'))
  ) {
    return Object.assign(new Error(message || 'App Check verification failed.'), {
      code: 'auth/app-check',
      details,
    });
  }
  const blocked = message.includes('blocked') || detailCode === 'blocked';
  const pending = message.includes('not trusted') || detailCode === 'pending_trust';
  return Object.assign(new Error(message || 'Device not authorized.'), {
    code: blocked ? 'auth/device-blocked' : pending ? 'auth/device-pending' : 'auth/device-passkey',
    details,
  });
}

export async function registerPasskeyOnDevice() {
  const begin = await callFunction('beginDevicePasskeyRegistration', {}) as {
    skipped?: boolean;
    options?: Parameters<typeof startRegistration>[0]['optionsJSON'];
    challengeId?: string;
  };
  if (begin.skipped) return { skipped: true };
  if (!begin.options || !begin.challengeId) {
    throw Object.assign(new Error('Passkey registration unavailable.'), { code: 'auth/device-passkey' });
  }

  const attResp = await startRegistration({ optionsJSON: begin.options });
  const platform = await resolveDevicePlatform();
  const identity = collectDeviceIdentity(platform);
  const result = await callFunction('finishDevicePasskeyRegistration', {
    challengeId: begin.challengeId,
    response: attResp,
    label: identity.label,
    platform: identity.platform,
    userAgent: identity.userAgent,
  }) as {
    deviceSessionToken?: string;
    deviceId?: string;
  };

  storeDeviceSession(result.deviceSessionToken, result.deviceId);
  return result;
}

export async function authenticatePasskeyOnDevice() {
  const begin = await callFunction('beginDevicePasskeyAuth', {}) as {
    skipped?: boolean;
    needsRegistration?: boolean;
    options?: Parameters<typeof startAuthentication>[0]['optionsJSON'];
    challengeId?: string;
  };
  if (begin.skipped) return { skipped: true };
  if (begin.needsRegistration) {
    return registerPasskeyOnDevice();
  }
  if (!begin.options || !begin.challengeId) {
    throw Object.assign(new Error('Passkey authentication unavailable.'), { code: 'auth/device-passkey' });
  }

  const assertion = await startAuthentication({ optionsJSON: begin.options });
  const platform = await resolveDevicePlatform();
  const identity = collectDeviceIdentity(platform);
  const result = await callFunction('finishDevicePasskeyAuth', {
    challengeId: begin.challengeId,
    response: assertion,
    label: identity.label,
    platform: identity.platform,
    userAgent: identity.userAgent,
  }) as {
    deviceSessionToken?: string;
    deviceId?: string;
  };

  storeDeviceSession(result.deviceSessionToken, result.deviceId);
  return result;
}

export async function ensureDevicePasskeySession() {
  try {
    return await authenticatePasskeyOnDevice();
  } catch (err) {
    throw denialError(err);
  }
}

export async function assertStoredDeviceSession(options?: { prompt?: boolean }) {
  const token = readSessionToken();
  try {
    await callFunction('assertDeviceSession', { deviceSessionToken: token ?? '' });
    return { allowed: true };
  } catch (err) {
    const details = (err as { details?: { requiresPasskey?: boolean; code?: string } })?.details;
    const needsPasskey = details?.requiresPasskey
      || details?.code === 'session_expired'
      || details?.code === 'session_missing';
    if (needsPasskey && options?.prompt !== false) {
      return ensureDevicePasskeySession();
    }
    throw denialError(err);
  }
}

export async function isWebAuthnPlatformAvailable(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
